import { useEffect } from 'react';
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react';
import { AlertCircle, Unlink } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { COLORE_RAMO, NOME_RAMO, eUnaPartenza } from '../../../lib/email/disegnoSequenza';
import { type StepKind } from '../../../types/automazioni.types';
import { ASPETTO_PASSI, NOME_CORTO } from './aspettoPassi';

export interface DatiNodo extends Record<string, unknown> {
  kind: StepKind | 'inizio';
  titolo: string;
  /** Una riga che dice cosa fa davvero questa casella. */
  riassunto: string;
  /** Cosa manca per poter accendere, gia' detto a parole. Null se niente. */
  manca?: string | null;
  /** Nessuna freccia arriva qui: nessun contatto ci passera' mai. */
  scollegata?: boolean;
  /** Solo "aspetta che entri in una lista": ha un tempo massimo, quindi
   *  anche l'uscita "Scaduto". */
  conScadenza?: boolean;
}

/** Il punto da cui si tira una freccia. Grande abbastanza da prenderlo. */
function stileAttacco(colore: string, sinistra?: string) {
  return {
    width: 12,
    height: 12,
    background: '#fff',
    border: `2px solid ${colore}`,
    ...(sinistra ? { left: sinistra } : {}),
  };
}

/** Le due uscite in fondo alla casella: a sinistra la prima, a destra la seconda. */
function DueUscite({ sinistra, destra }: { sinistra: string; destra: string | null }) {
  return (
    <>
      {/* Due colonne anche con un'uscita sola: il nome resta sopra il suo attacco. */}
      <div className="grid grid-cols-2 border-t border-neutral-100 text-[10.5px] font-semibold text-center py-1">
        <span style={{ color: COLORE_RAMO[sinistra] }}>{NOME_RAMO[sinistra]}</span>
        {destra ? (
          <span style={{ color: COLORE_RAMO[destra] }}>{NOME_RAMO[destra]}</span>
        ) : (
          <span className="font-normal text-neutral-400">senza scadenza</span>
        )}
      </div>
      <Handle
        id={sinistra}
        type="source"
        position={Position.Bottom}
        style={stileAttacco(COLORE_RAMO[sinistra], '25%')}
      />
      {destra && (
        <Handle
          id={destra}
          type="source"
          position={Position.Bottom}
          style={stileAttacco(COLORE_RAMO[destra], '75%')}
        />
      )}
    </>
  );
}

/**
 * Una casella sulla lavagna.
 *
 * Il riquadro colorato dice il tipo, il titolo dice quale, la riga sotto
 * dice cosa fa. Se manca qualcosa per accendere, lo dice in fondo in rosso:
 * chi guarda il percorso capisce dove mettere le mani senza aprire niente.
 */
export function NodoPasso({ id, data, selected }: NodeProps) {
  const dati = data as DatiNodo;
  // Comparsa o sparita l'uscita "Scaduto", la lavagna deve sapere dove sono
  // gli attacchi, altrimenti non ci si puo' tirare una freccia.
  const aggiornaAttacchi = useUpdateNodeInternals();
  useEffect(() => {
    aggiornaAttacchi(id);
  }, [id, dati.conScadenza, aggiornaAttacchi]);
  const aspetto = ASPETTO_PASSI[dati.kind];
  const Icona = aspetto.icona;
  const partenza = dati.kind === 'inizio';
  const tipo = dati.kind === 'inizio' ? 'Partenza' : NOME_CORTO[dati.kind];
  const dueUscite = dati.kind === 'condizione' || dati.kind === 'attendi_evento';
  // Con due uscite la casella chiude in basso con i loro nomi: la striscia di
  // avviso sta sopra.
  const angoliStriscia = dueUscite ? '' : 'rounded-b-md';

  return (
    <div
      className={cn(
        'w-[240px] rounded-md border shadow-[0_1px_2px_rgba(0,0,0,0.06)] transition-[box-shadow,border-color] duration-150',
        partenza ? 'bg-neutral-900 border-neutral-900' : 'bg-white',
        selected
          ? cn('ring-2 ring-offset-2 ring-offset-neutral-50 border-transparent shadow-lg', aspetto.anello)
          : !partenza && 'border-neutral-200 hover:border-neutral-300 hover:shadow-md'
      )}
    >
      {/* Alle partenze ("Si parte", "ogni volta") non arriva nessuna freccia. */}
      {!eUnaPartenza(dati.kind) && (
        <Handle type="target" position={Position.Top} style={stileAttacco('#a3a3a3')} />
      )}

      <div className="flex items-start gap-3 p-3">
        <div
          className={cn(
            'w-9 h-9 flex items-center justify-center flex-shrink-0',
            partenza ? 'bg-white/10 text-white' : aspetto.pieno
          )}
        >
          <Icona className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'text-[10px] font-semibold uppercase tracking-[0.08em] leading-none mb-1',
              partenza ? 'text-white/50' : aspetto.testo
            )}
          >
            {tipo}
          </p>
          <p
            className={cn(
              'text-[13px] font-semibold leading-tight truncate',
              partenza ? 'text-white' : 'text-neutral-900'
            )}
          >
            {dati.titolo}
          </p>
          <p
            className={cn(
              'text-[11.5px] leading-snug line-clamp-2 break-words mt-0.5',
              partenza ? 'text-white/70' : 'text-neutral-500'
            )}
            title={dati.riassunto}
          >
            {dati.riassunto}
          </p>
        </div>
      </div>

      {dati.manca ? (
        <div className={cn('flex items-center gap-1.5 border-t border-red-100 bg-red-50 px-3 py-1.5 text-[11px] font-medium text-red-700', angoliStriscia)}>
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          {dati.manca}
        </div>
      ) : dati.scollegata ? (
        <div className={cn('flex items-center gap-1.5 border-t border-amber-100 bg-amber-50 px-3 py-1.5 text-[11px] font-medium text-amber-800', angoliStriscia)}>
          <Unlink className="w-3.5 h-3.5 flex-shrink-0" />
          Non collegata: tira una freccia fin qui
        </div>
      ) : null}

      {/* Una domanda ha il si' a sinistra e il no a destra; l'attesa di una
          lista "Arrivata" e, se ha un tempo massimo, "Scaduto". */}
      {dati.kind === 'condizione' ? (
        <DueUscite sinistra="si" destra="no" />
      ) : dati.kind === 'attendi_evento' ? (
        <DueUscite sinistra="arrivata" destra={dati.conScadenza ? 'scaduta' : null} />
      ) : dati.kind !== 'fine' ? (
        <Handle
          id="out"
          type="source"
          position={Position.Bottom}
          style={stileAttacco(partenza ? '#171717' : '#a3a3a3')}
        />
      ) : null}
    </div>
  );
}
